import { z } from 'zod';
// Les mêmes règles de saisie servent à l'API et aux formulaires de l'interface.
export const modeDossier = z.enum(['GYNECOLOGIE_BENIGNE', 'OBSTETRIQUE', 'ONCOLOGIE']);
export const sexe = z.enum(['FEMME', 'HOMME', 'AUTRE', 'NON_RENSEIGNE']);
export const typeVenue = z.enum(['CONSULTATION', 'HOSPITALISATION', 'INTERVENTION', 'AUTRE']);
export const typeDocument = z.enum([
    'INDETERMINE',
    'COMPTE_RENDU_CONSULTATION',
    'COMPTE_RENDU_OPERATOIRE',
    'COMPTE_RENDU_HOSPITALISATION',
    'COMPTE_RENDU_IMAGERIE',
    'COMPTE_RENDU_ANAPATH',
    'BILAN_BIOLOGIQUE',
    'COURRIER',
    'ORDONNANCE',
    'FICHE_BLOC',
    'DEMANDE_ANAPATH',
    'AUTRE',
]);
export const precisionDate = z.enum(['JOUR', 'MOIS', 'ANNEE', 'INCERTAINE']);
export const typeAntecedent = z.enum([
    'MEDICAL',
    'CHIRURGICAL',
    'GYNECOLOGIQUE',
    'OBSTETRICAL',
    'FAMILIAL',
    'MODE_DE_VIE',
]);
export const statutTraitement = z.enum(['ACTUEL', 'ARRETE', 'ANCIEN']);
export const severite = z.enum(['INFO', 'ATTENTION', 'CRITIQUE']);
/** Une date saisie à la main : « 2026-03-14 », ou rien. */
const dateOptionnelle = z
    .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal(''), z.null()])
    .optional()
    .transform((v) => (v ? v : null));
const texteOptionnel = z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => {
    const t = typeof v === 'string' ? v.trim() : '';
    return t.length > 0 ? t : null;
});
// --- Connexion --------------------------------------------------------------
export const demanderCodeSchema = z.object({
    email: z.string().trim().toLowerCase().email("Cette adresse e-mail n'est pas valide"),
});
export const verifierCodeSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    code: z.string().trim().regex(/^\d{6}$/, 'Le code compte six chiffres'),
});
// --- Médecin ----------------------------------------------------------------
export const profilMedecinSchema = z.object({
    civilite: texteOptionnel,
    nom: z.string().trim().min(1, 'Le nom est nécessaire'),
    prenom: z.string().trim().min(1, 'Le prénom est nécessaire'),
    specialite: texteOptionnel,
    rpps: texteOptionnel,
    adeli: texteOptionnel,
    adresseLigne1: texteOptionnel,
    adresseLigne2: texteOptionnel,
    codePostal: texteOptionnel,
    ville: texteOptionnel,
    telephone: texteOptionnel,
    emailPro: texteOptionnel,
});
// --- Patient ----------------------------------------------------------------
export const identitePatientSchema = z.object({
    nom: texteOptionnel,
    nomNaissance: texteOptionnel,
    prenom: texteOptionnel,
    dateNaissance: dateOptionnelle,
    sexe: sexe.optional(),
    telephone: texteOptionnel,
    email: texteOptionnel,
    adresse: texteOptionnel,
    notes: texteOptionnel,
});
export const modeAffichageSchema = z.object({ modeAffichage: modeDossier });
export const fusionSchema = z.object({
    sourceId: z.string().uuid(),
    cibleId: z.string().uuid(),
    // Identité conservée champ par champ : « source » prend la valeur du dossier
    // absorbé, « cible » garde celle du dossier conservé.
    identite: z.record(z.enum(['source', 'cible'])).default({}),
});
// --- Venue ------------------------------------------------------------------
export const venueSchema = z.object({
    date: z.string().min(1, 'La date est nécessaire'),
    type: typeVenue,
    motif: texteOptionnel,
    notes: texteOptionnel,
    conduiteATenir: texteOptionnel,
    diagnosticIds: z.array(z.string().uuid()).optional().default([]),
});
// --- Données médicales ------------------------------------------------------
export const antecedentSchema = z.object({
    libelle: z.string().trim().min(1, 'Le libellé est nécessaire'),
    type: typeAntecedent.default('MEDICAL'),
    date: dateOptionnelle,
    precisionDate: precisionDate.default('JOUR'),
    cote: texteOptionnel,
    precisions: texteOptionnel,
});
export const allergieSchema = z.object({
    substance: z.string().trim().min(1, 'La substance est nécessaire'),
    reaction: texteOptionnel,
    gravite: texteOptionnel,
    precisions: texteOptionnel,
});
export const diagnosticSchema = z.object({
    libelle: z.string().trim().min(1, 'Le libellé est nécessaire'),
    code: texteOptionnel,
    dateDiagnostic: dateOptionnelle,
    precisionDate: precisionDate.default('JOUR'),
    actif: z.boolean().default(true),
    precisions: texteOptionnel,
});
export const traitementSchema = z.object({
    denomination: z.string().trim().min(1, 'Le nom du traitement est nécessaire'),
    dci: texteOptionnel,
    dosage: texteOptionnel,
    posologie: texteOptionnel,
    voie: texteOptionnel,
    statut: statutTraitement.default('ACTUEL'),
    dateDebut: dateOptionnelle,
    precisionDate: precisionDate.default('JOUR'),
    dateFin: dateOptionnelle,
    indication: texteOptionnel,
    prescripteur: texteOptionnel,
});
export const evenementSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La date est nécessaire'),
    precisionDate: precisionDate.default('JOUR'),
    libelle: z.string().trim().min(1, 'Le libellé est nécessaire'),
    description: texteOptionnel,
    categorie: texteOptionnel,
});
export const donneeSpecifiqueSchema = z.object({
    domaine: z.enum(['obstetrique', 'oncologie']),
    libelle: z.string().trim().min(1, 'Le libellé est nécessaire'),
    valeur: texteOptionnel,
    date: dateOptionnelle,
});
export const detailParCategorie = {
    ANTECEDENT: antecedentSchema,
    ALLERGIE: allergieSchema,
    DIAGNOSTIC: diagnosticSchema,
    TRAITEMENT: traitementSchema,
    EVENEMENT_HISTOIRE: evenementSchema,
    DONNEE_SPECIFIQUE: donneeSpecifiqueSchema,
};
// --- Documents --------------------------------------------------------------
export const texteColleSchema = z.object({
    titre: z.string().trim().min(1, 'Un titre est nécessaire'),
    texte: z.string().min(1, 'Le texte est vide'),
    type: typeDocument.default('INDETERMINE'),
    dateDocument: dateOptionnelle,
    precisionDateDocument: precisionDate.default('JOUR'),
});
export const metaDocumentSchema = z.object({
    titre: z.string().trim().min(1).optional(),
    type: typeDocument.optional(),
    dateDocument: dateOptionnelle,
    precisionDateDocument: precisionDate.optional(),
    venueId: z.union([z.string().uuid(), z.null()]).optional(),
});
// --- Tâches -----------------------------------------------------------------
export const tacheSchema = z.object({
    libelle: z.string().trim().min(1, 'Le libellé est nécessaire'),
    patientId: z.union([z.string().uuid(), z.null()]).optional(),
    echeance: dateOptionnelle,
});
//# sourceMappingURL=schemas.js.map